import { useState } from "react";
import { Loader2, PackageSearch, CheckCircle2, Circle } from "lucide-react";
import StatusBadge from "../../components/StatusBadge";
import api, { fmtErr, fmtDateTime, fmtDate } from "../../lib/api";
import ShareTrackingActions from "../../components/ShareTrackingActions";
import { toast } from "sonner";

export default function Track() {
  const [awb, setAwb] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e?.preventDefault();
    if (!awb.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/track/${awb.trim().toUpperCase()}`);
      setData(data);
    } catch (err) {
      setData(null);
      toast.error(fmtErr(err, "Tracking lookup failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="app-track-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Track Shipment</h1>
      <p className="text-sm text-slate-500">Live normalized tracking for any SHIPANY AWB.</p>

      <form onSubmit={track} className="mt-6 flex gap-3">
        <input data-testid="app-track-input" value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="Enter AWB number"
          className="mono flex-1 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15" />
        <button data-testid="app-track-btn" disabled={loading}
          className="flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60 glow-blue">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageSearch className="h-4 w-4" />} Track
        </button>
      </form>

      {data && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-7" data-testid="app-track-result">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="mono text-xl font-bold text-shipany-navy">{data.awb}</p>
              <p className="mt-1 text-sm text-slate-500">{data.courier_name} · {data.origin_city} → {data.destination_city} · ETA {fmtDate(data.eta_date)}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ShareTrackingActions code={data.awb} label="shipment" testId="share-app-track" />
              <StatusBadge status={data.status} />
            </div>
          </div>
          {data.receiver && (data.receiver.name || data.receiver.city) && (
            <div className="mt-4 rounded-xl bg-slate-50 p-4" data-testid="app-track-receiver">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Receiver</p>
              <p className="mt-1 text-sm font-bold text-shipany-navy" data-testid="app-track-receiver-name">{data.receiver.name}</p>
              <p className="text-xs text-slate-400">{[data.receiver.city, data.receiver.state, data.receiver.pincode].filter(Boolean).join(", ")}</p>
              {data.receiver.phone && <p className="mono mt-0.5 text-xs text-slate-400" data-testid="app-track-receiver-phone">{data.receiver.phone}</p>}
            </div>
          )}
          <div className="mt-6">
            {data.forwarding_leg && (
              <div className="mb-6" data-testid="app-track-domestic-leg">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Leg 1 · Domestic transfer to international hub</p>
                  <StatusBadge status={data.forwarding_leg.status} />
                </div>
                <p className="mono mt-1 text-xs text-slate-400">AWB {data.forwarding_leg.awb}{data.forwarding_leg.courier_name ? ` · ${data.forwarding_leg.courier_name}` : ""}</p>
                <div className="mt-3">
                  {data.forwarding_leg.events.map((e, i) => (
                    <div key={i} className="relative flex gap-4 pb-4 last:pb-0">
                      {i < data.forwarding_leg.events.length - 1 && <span className="absolute left-[10px] top-5 h-full w-0.5 bg-slate-200" />}
                      {e.completed ? <CheckCircle2 className="relative z-10 h-5 w-5 shrink-0 text-emerald-500" /> : <Circle className="relative z-10 h-5 w-5 shrink-0 text-slate-300" />}
                      <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className={`text-sm font-semibold ${e.completed ? "text-shipany-navy" : "text-slate-400"}`}>{e.note || e.status.replace(/_/g, " ")}</p>
                          <p className="text-xs text-slate-400">{e.location || ""}</p>
                        </div>
                        <p className="mono text-xs text-slate-400">{fmtDateTime(e.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 border-t border-slate-100 pt-3 text-xs font-bold uppercase tracking-widest text-slate-400">Leg 2 · International journey</p>
              </div>
            )}
            {data.events.map((e, i) => (
              <div key={i} className="relative flex gap-4 pb-5 last:pb-0">
                {i < data.events.length - 1 && <span className="absolute left-[10px] top-5 h-full w-0.5 bg-slate-200" />}
                {e.completed ? <CheckCircle2 className="relative z-10 h-5 w-5 shrink-0 text-emerald-500" /> : <Circle className="relative z-10 h-5 w-5 shrink-0 text-slate-300" />}
                <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className={`text-sm font-semibold ${e.completed ? "text-shipany-navy" : "text-slate-400"}`}>{e.note || e.status.replace(/_/g, " ")}</p>
                    <p className="text-xs text-slate-400">{e.location || ""}</p>
                  </div>
                  <p className="mono text-xs text-slate-400">{fmtDateTime(e.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
